import "../styles/AdminInterface.css"
import React, { useState, useEffect, useMemo } from 'react'
import BackendAPI from "../API/BackendAPI"
import IconButton from '@mui/material/IconButton';

import { useHistory } from 'react-router-dom';
import { FormControl, InputLabel, Tooltip, Divider, Typography, TableContainer, TableHead, TableRow, Paper, TableBody, Table, TableCell, Select, MenuItem, Button } from "@material-ui/core"
import DownloadIcon from '@mui/icons-material/Download';

import Sidebar from "../components/Sidebar"
import cookie from 'react-cookies'
import { formatDate, formatDuree, telechargerCSV, aujourdhui } from "../utils/csv"
import { LISTE_JEUX, NOMS_JEUX, jeuVersNum, optionsAnnees, anneeEnCours, dansAnnee, appliquerFiltres } from "../utils/filtres"


/** Temps de jeu au format de l'export Play & Learn : 3725 s → "01h02" */
const heuresMinutes = (secondes) => {
  const t = Math.max(0, Math.round(Number(secondes) || 0))
  return `${String(Math.floor(t / 3600)).padStart(2, "0")}h${String(Math.floor((t % 3600) / 60)).padStart(2, "0")}`
}

/** La plus récente de deux dates « AAAA-MM-JJ » (null si aucune). */
const plusRecente = (a, b) => (!a ? (b || null) : (!b ? a : (String(b) > String(a) ? b : a)))

// ─────────────────────────────────────────────────────────────
// Statistiques des jeux, comme la page « Stats Jeux » de l'administration
// Play & Learn : nombre de parties, temps de jeu et dernière partie par
// étudiant, sur un jeu ou sur tous ; mêmes filtres, même export CSV.
// ─────────────────────────────────────────────────────────────
const AdminStatistiquesJeux = () => {
  const history = useHistory()

  const user = cookie.load("user")
  const handleDisconnect = () => {
    cookie.save('user', "disconnected", {
      path: '/',
      maxAge: 60 * 60 * 1.5, //sec
    })
    history.push('/login');
  }

  if (user === null || user === undefined || user === "disconnected") history.push("/login");
  if (user !== null && user !== undefined && user.username !== undefined && !user.username.toUpperCase().includes("ADMIN")) handleDisconnect()

  // Une ligne par étudiant et par jeu joué ; null : chargement ou erreur
  const [stats, setStats] = useState(null)
  const [etat, setEtat] = useState('chargement')   // 'chargement' | 'ok' | 'erreur'
  const [rechargement, setRechargement] = useState(0)

  const anneesOptions = useMemo(() => optionsAnnees(), [])
  // Année en cours par défaut, comme Play & Learn.
  const [annee, setAnnee] = useState(anneeEnCours())
  const [jeu, setJeu] = useState("Tous")
  const [nom, setNom] = useState('Tous');
  const [prenom, setPrenom] = useState('Tous');
  const [pseudo, setPseudo] = useState('Tous');
  const [campus, setCampus] = useState('Tous');
  const [programme, setProgramme] = useState('Tous');
  const [groupe, setGroupe] = useState('Tous');
  const [survol, setSurvol] = useState(null)

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
  };

  useEffect(() => {
    let actif = true
    const charger = async () => {
      setEtat('chargement')
      const donnees = await BackendAPI.getStatistiquesJeux()
      if (!actif) return
      setStats(donnees)
      setEtat(donnees === null ? 'erreur' : 'ok')
    }
    charger()
    return () => { actif = false }
  }, [rechargement])

  // Jeu, puis année : date de la dernière partie de chaque jeu (même règle
  // que Play & Learn).
  const lignesPeriode = useMemo(() => {
    const num = jeuVersNum(jeu)
    return (stats || [])
      .filter((l) => num === null || Number(l.numJeu) === num)
      .filter((l) => dansAnnee(l.dernierePartie, annee))
  }, [stats, jeu, annee])

  const { options, retenues } = useMemo(
    () => appliquerFiltres(lignesPeriode, { campus, programme, groupe, nom, prenom, pseudo }),
    [lignesPeriode, campus, programme, groupe, nom, prenom, pseudo])

  // Un filtre dont la valeur n'est plus proposée revient à « Tous ».
  useEffect(() => { if (!options.campus.includes(campus)) setCampus("Tous") }, [options.campus, campus])
  useEffect(() => { if (!options.programme.includes(programme)) setProgramme("Tous") }, [options.programme, programme])
  useEffect(() => { if (!options.groupe.includes(groupe)) setGroupe("Tous") }, [options.groupe, groupe])
  useEffect(() => { if (!options.nom.includes(nom)) setNom("Tous") }, [options.nom, nom])
  useEffect(() => { if (!options.prenom.includes(prenom)) setPrenom("Tous") }, [options.prenom, prenom])
  useEffect(() => { if (!options.pseudo.includes(pseudo)) setPseudo("Tous") }, [options.pseudo, pseudo])

  // Une ligne par étudiant : parties et temps additionnés sur les jeux
  // retenus, dernière partie la plus récente, détail par jeu pour le survol.
  const lignes = useMemo(() => {
    const parEtudiant = {}
    retenues.forEach((l) => {
      const nb = Number(l.nbParties) || 0
      const temps = Number(l.tempsJeu) || 0
      if (!parEtudiant[l.idUser]) {
        parEtudiant[l.idUser] = { ...l, nbParties: 0, tempsJeu: 0, dernierePartie: null, detail: [] }
      }
      const e = parEtudiant[l.idUser]
      e.nbParties += nb
      e.tempsJeu += temps
      e.dernierePartie = plusRecente(e.dernierePartie, l.dernierePartie)
      e.detail.push({ numJeu: Number(l.numJeu), nbParties: nb, tempsJeu: temps, dernierePartie: l.dernierePartie })
    })
    return Object.values(parEtudiant)
      .map((e) => ({ ...e, detail: e.detail.sort((a, b) => a.numJeu - b.numJeu) }))
      .sort((a, b) => b.nbParties - a.nbParties ||
        String(a.nom).localeCompare(String(b.nom)) || String(a.prenom).localeCompare(String(b.prenom)))
  }, [retenues])

  const totalParties = lignes.reduce((t, l) => t + l.nbParties, 0)
  const totalTemps = lignes.reduce((t, l) => t + l.tempsJeu, 0)
  const libelleAnnee = annee === "Tous" ? "Toutes années" : annee

  const exportToCSV = () => {
    if (lignes.length === 0) {
      alert("Aucun utilisateur à exporter !");
      return;
    }
    // Colonnes de l'export « Stats Jeux » de Play & Learn, plus le temps en
    // secondes (pour faire des totaux dans Excel).
    const headers = ["Nom", "Prénom", "Pseudo", "Email", "Campus", "Programme", "Groupe", "Nombre de parties", "Temps de jeu", "Date dernière partie", "Temps de jeu (s)"];
    const rows = lignes.map((u) => [
      u.nom, u.prenom, u.pseudo, u.email, u.campus, u.programme, u.groupe,
      u.nbParties, heuresMinutes(u.tempsJeu), u.dernierePartie || "", u.tempsJeu,
    ]);
    telechargerCSV(
      "Statistiques jeux - " + (jeu === "Tous" ? "tous les jeux" : jeu) + " - " + libelleAnnee + " - " + aujourdhui() + ".csv",
      headers,
      rows
    )
  };

  const renderDetail = (u) => (
    <span style={{ fontSize: 13, lineHeight: 1.5 }}>
      {u.detail.map((d) => (
        <span key={d.numJeu}>
          <b>{NOMS_JEUX[d.numJeu] || `Jeu ${d.numJeu - 10}`}</b> : {d.nbParties} partie{d.nbParties > 1 ? "s" : ""} · {formatDuree(d.tempsJeu)}
          {d.dernierePartie ? ` · dernière le ${formatDate(d.dernierePartie)}` : ""}<br />
        </span>
      ))}
    </span>
  )

  const renderSelect = (label, value, setter, opts) => (
    <FormControl size="small" variant="outlined">
      <InputLabel>{label}</InputLabel>
      <Select value={value} onChange={handleFilterChange(setter)} label={label}>
        {opts.map((option) => (
          <MenuItem key={option} value={option} style={{ paddingTop: 8, paddingBottom: 8 }}>{option}</MenuItem>
        ))}
      </Select>
    </FormControl>
  )

  const renderTableau = () => {
    if (etat === 'chargement') return <Typography color="textSecondary">Chargement des statistiques…</Typography>
    if (etat === 'erreur') return (
      <Typography color="error" component="div">
        Les statistiques n'ont pas pu être chargées (serveur injoignable).{" "}
        <Button size="small" color="primary" variant="outlined" onClick={() => setRechargement((n) => n + 1)}>Réessayer</Button>
      </Typography>
    )
    if (lignes.length === 0) return <Typography color="textSecondary">Aucune partie pour ces filtres.</Typography>
    return (
      <TableContainer component={Paper} style={{ maxHeight: "100%" }} >
        <Table stickyHeader aria-label="statistiques jeux">
          <TableHead>
            <TableRow key={"HEAD"}>
              <TableCell align="center" style={{ width: 150 }}>Nom</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Prénom</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Pseudo</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Campus</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Programme</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Groupe</TableCell>
              <TableCell align="center" style={{ width: 200 }}>Nombre parties</TableCell>
              <TableCell align="center" style={{ width: 200 }}>Temps jeu</TableCell>
              <TableCell align="center" style={{ width: 200 }}>Dernière partie</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lignes.map((u) => (
              <TableRow key={u.idUser} style={{ height: 40 }}>
                <TableCell align="center">{u.nom}</TableCell>
                <TableCell align="center">{u.prenom}</TableCell>
                <TableCell align="center">{u.pseudo}</TableCell>
                <TableCell align="center">{u.campus}</TableCell>
                <TableCell align="center">{u.programme}</TableCell>
                <TableCell align="center">{u.groupe}</TableCell>
                <TableCell align="center" style={{ cursor: "pointer" }}>
                  <Tooltip title={survol === u.idUser ? renderDetail(u) : ''} placement="right" arrow>
                    <span onMouseEnter={() => setSurvol(u.idUser)} onMouseLeave={() => setSurvol(null)}>
                      {u.nbParties}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">{formatDuree(u.tempsJeu)}</TableCell>
                <TableCell align="center">{formatDate(u.dernierePartie)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div
        className="body-admin"
      >
        <Typography variant="h1" color="primary">Statistiques Jeux</Typography>
        <Divider />
        <div style={{ margin: 10 }}  >
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            {renderSelect("Année", annee, setAnnee, anneesOptions)}
            {renderSelect("Jeu", jeu, setJeu, LISTE_JEUX)}
            {renderSelect("Nom", nom, setNom, options.nom)}
            {renderSelect("Prénom", prenom, setPrenom, options.prenom)}
            {renderSelect("Pseudo", pseudo, setPseudo, options.pseudo)}
            {renderSelect("Campus", campus, setCampus, options.campus)}
            {renderSelect("Programme", programme, setProgramme, options.programme)}
            {renderSelect("Groupe", groupe, setGroupe, options.groupe)}
            <Tooltip title="Exporter les statistiques affichées (CSV)" arrow>
              <IconButton aria-label="download" style={{ padding: 10 }} onClick={exportToCSV}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </div>
          <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: -10 }}>
            Année : jeux dont la dernière partie date de cette année scolaire (25 août → 24 août), comme Play & Learn ; parties et temps sont cumulés depuis la première partie.
            Survol du nombre de parties : détail par jeu. Avant la version 3.2.0 de l'application, le temps comptait depuis l'ouverture du jeu (parties rejouées comprises).
          </Typography>
          {etat === 'ok' && lignes.length > 0 ?
            <Typography variant="body2" style={{ marginTop: 10 }}>
              <b>{lignes.length}</b> étudiant{lignes.length > 1 ? "s" : ""} · <b>{totalParties}</b> partie{totalParties > 1 ? "s" : ""} · <b>{formatDuree(totalTemps)}</b> de jeu
            </Typography>
            : null}
          <br></br>
          {renderTableau()}
        </div>
      </div></div>
  );
}

export default AdminStatistiquesJeux;
